import React from "react";
import { Link } from "react-router-dom";
import "./theme-previews.css";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  ArrowLeft, Truck, HardHat, TriangleAlert, Zap, Gauge, Boxes, Wrench, Clock,
} from "lucide-react";

const THEMES = {
  v1: {
    id: "1",
    className: "theme-v1",
    name: "Concrete & Volt",
    mode: "Light Industrial",
    blurb: "Concrete-grey canvas, ink primary, Volt-Green high-vis accent, razor-sharp 2px corners and hard brutalist shadows.",
    swatches: [
      ["Background", "#F8F9FA"], ["Ink / Primary", "#1A1A1A"],
      ["Volt Green", "#CCFF00"], ["Safety Orange", "#FF4F00"],
    ],
  },
  v2: {
    id: "2",
    className: "theme-v2",
    name: "Blueprint Dark",
    mode: "Dark Industrial",
    blurb: "Deep blueprint navy, electric-cyan primary, hi-vis amber accent, 4px corners and a cyan hard-shadow edge.",
    swatches: [
      ["Background", "#0B1220"], ["Electric Cyan", "#22D3EE"],
      ["Hi-Vis Amber", "#F2C94C"], ["Coral Alert", "#FB7185"],
    ],
  },
};

const StatusPill = ({ color, label }) => (
  <span
    className="inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs font-mono"
    style={{ borderColor: "hsl(var(--border))" }}
    data-testid={`status-pill-${label.toLowerCase()}`}
  >
    <span className="theme-dot" style={{ backgroundColor: color }} />
    {label}
  </span>
);

const Gallery = ({ theme }) => {
  return (
    <div className={`${theme.className} theme-canvas font-body`} data-testid={`theme-canvas-${theme.id}`}>
      {/* Header */}
      <header
        className="theme-noise border-b px-6 md:px-10 py-8"
        style={{ borderColor: "hsl(var(--border))", background: "hsl(var(--card))" }}
      >
        <div className="max-w-6xl">
          <div className="flex items-center justify-between gap-4">
            <Link
              to="/theme-preview"
              className="theme-overline inline-flex items-center gap-2 hover:opacity-70"
              style={{ color: "hsl(var(--muted-foreground))" }}
              data-testid="back-to-index-link"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} /> All Themes
            </Link>
            <span className="theme-overline" style={{ color: "hsl(var(--muted-foreground))" }}>
              Theme Option {theme.id} · {theme.mode}
            </span>
          </div>
          <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl mt-5">
            {theme.name}
          </h1>
          <p className="mt-3 max-w-2xl text-base" style={{ color: "hsl(var(--muted-foreground))" }}>
            {theme.blurb}
          </p>
          {/* Swatches */}
          <div className="mt-6 flex flex-wrap gap-4">
            {theme.swatches.map(([label, hex]) => (
              <div key={label} className="flex items-center gap-2">
                <span
                  className="h-7 w-7 rounded-sm border"
                  style={{ backgroundColor: hex, borderColor: "hsl(var(--border))" }}
                />
                <span className="text-xs">
                  <span className="block font-medium">{label}</span>
                  <span className="font-mono" style={{ color: "hsl(var(--muted-foreground))" }}>{hex}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10 max-w-6xl space-y-10">
        {/* Buttons */}
        <section>
          <p className="theme-overline mb-3" style={{ color: "hsl(var(--muted-foreground))" }}>Actions</p>
          <div className="flex flex-wrap gap-3 items-center">
            <Button className="font-heading uppercase tracking-wide" data-testid="btn-primary">
              <HardHat className="h-4 w-4 mr-2" strokeWidth={2} /> Primary
            </Button>
            <Button
              className="font-heading uppercase tracking-wide"
              style={{ backgroundColor: "hsl(var(--accent))", color: "hsl(var(--accent-foreground))" }}
              data-testid="btn-allocate"
            >
              <Zap className="h-4 w-4 mr-2" strokeWidth={2} /> Allocate
            </Button>
            <Button variant="secondary" data-testid="btn-secondary">Secondary</Button>
            <Button variant="outline" data-testid="btn-outline">Outline</Button>
            <Button variant="destructive" data-testid="btn-destructive">
              <TriangleAlert className="h-4 w-4 mr-2" strokeWidth={2} /> Conflict
            </Button>
            <Button variant="ghost" data-testid="btn-ghost">Ghost</Button>
          </div>
        </section>

        {/* Bento cards */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {/* Metric */}
          <Card className="border" data-testid="card-metric">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <span className="theme-overline" style={{ color: "hsl(var(--muted-foreground))" }}>Fleet Utilisation</span>
                <Gauge className="h-4 w-4" strokeWidth={2} style={{ color: "hsl(var(--muted-foreground))" }} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="font-mono text-4xl font-medium">82%</div>
              <Progress value={82} className="mt-3 h-2" />
              <p className="text-xs mt-2" style={{ color: "hsl(var(--muted-foreground))" }}>18 of 22 assets booked today</p>
            </CardContent>
          </Card>

          {/* Allocation card */}
          <Card className="border md:col-span-2" data-testid="card-allocation">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Truck className="h-5 w-5" strokeWidth={2} /> Job #CT-4471 — Nudgee Haul
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <StatusPill color="#22C55E" label="Confirmed" />
                <StatusPill color="#F59E0B" label="Allocated" />
                <StatusPill color="#EF4444" label="Conflict" />
              </div>
              <Separator style={{ backgroundColor: "hsl(var(--border))" }} />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                {[["CLIENT ID", "ARN-018"], ["ASSET", "PM-22"], ["WINDOW", "07:00–15:00"], ["RATE", "$185/hr"]].map(([k, v]) => (
                  <div key={k}>
                    <div className="theme-overline" style={{ color: "hsl(var(--muted-foreground))" }}>{k}</div>
                    <div className="font-mono mt-1">{v}</div>
                  </div>
                ))}
              </div>
              <div className="pt-1">
                <Button
                  size="sm"
                  className="font-heading uppercase tracking-wide"
                  style={{ backgroundColor: "hsl(var(--accent))", color: "hsl(var(--accent-foreground))" }}
                  data-testid="btn-dispatch"
                >
                  <Zap className="h-4 w-4 mr-2" strokeWidth={2} /> Dispatch Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Form + tabs */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border" data-testid="card-form">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Wrench className="h-5 w-5" strokeWidth={2} /> Asset Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="theme-overline" style={{ color: "hsl(var(--muted-foreground))" }}>Asset ID</Label>
                <Input defaultValue="PM-22" className="font-mono" data-testid="input-asset-id" />
              </div>
              <div className="space-y-1.5">
                <Label className="theme-overline" style={{ color: "hsl(var(--muted-foreground))" }}>Description</Label>
                <Input placeholder="Prime Mover — Kenworth T610" data-testid="input-desc" />
              </div>
              <div className="flex items-center justify-between rounded-sm border p-3" style={{ borderColor: "hsl(var(--border))" }}>
                <span className="text-sm">Available for allocation</span>
                <Switch defaultChecked data-testid="switch-available" />
              </div>
            </CardContent>
          </Card>

          <Card className="border" data-testid="card-tabs">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Boxes className="h-5 w-5" strokeWidth={2} /> Docket</CardTitle></CardHeader>
            <CardContent>
              <Tabs defaultValue="hours">
                <TabsList data-testid="preview-tabs">
                  <TabsTrigger value="hours" data-testid="tab-hours">Hours</TabsTrigger>
                  <TabsTrigger value="materials" data-testid="tab-materials">Materials</TabsTrigger>
                  <TabsTrigger value="invoice" data-testid="tab-invoice">Invoice</TabsTrigger>
                </TabsList>
                <TabsContent value="hours" className="pt-4">
                  <div className="flex items-center gap-2 text-sm"><Clock className="h-4 w-4" strokeWidth={2} /> <span className="font-mono">8.0h billable</span> · portal-to-portal</div>
                </TabsContent>
                <TabsContent value="materials" className="pt-4 text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>
                  Clean fill — Disposal — 12 tonne
                </TabsContent>
                <TabsContent value="invoice" className="pt-4 font-mono text-sm">
                  Subtotal $1,480.00 · GST $148.00 · <b>Total $1,628.00</b>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </section>

        {/* Table */}
        <section>
          <p className="theme-overline mb-3" style={{ color: "hsl(var(--muted-foreground))" }}>Today's Runs</p>
          <Card className="border overflow-hidden" data-testid="card-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="theme-overline">Docket</TableHead>
                  <TableHead className="theme-overline">Asset</TableHead>
                  <TableHead className="theme-overline">Window</TableHead>
                  <TableHead className="theme-overline">Status</TableHead>
                  <TableHead className="theme-overline text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="font-mono text-sm">
                {[
                  ["DOC-4471", "PM-22", "07:00–15:00", "Confirmed", "#22C55E", "$1,628.00"],
                  ["DOC-4472", "EX-08", "08:30–12:00", "Allocated", "#F59E0B", "$940.00"],
                  ["DOC-4473", "CR-31", "06:00–18:00", "Conflict", "#EF4444", "$3,210.00"],
                ].map((r) => (
                  <TableRow key={r[0]} data-testid={`row-${r[0]}`}>
                    <TableCell>{r[0]}</TableCell>
                    <TableCell>{r[1]}</TableCell>
                    <TableCell>{r[2]}</TableCell>
                    <TableCell><StatusPill color={r[4]} label={r[3]} /></TableCell>
                    <TableCell className="text-right">{r[5]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>

        {/* Alert with brutalist shadow */}
        <section>
          <div
            className="brutal-shadow border p-4 flex items-start gap-3"
            style={{ borderColor: "hsl(var(--foreground))", background: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}
            data-testid="preview-alert"
          >
            <TriangleAlert className="h-5 w-5 mt-0.5 shrink-0" strokeWidth={2} />
            <div>
              <div className="font-heading font-bold uppercase tracking-wide">Scheduling Conflict</div>
              <div className="text-sm opacity-90">CR-31 is double-booked between 14:00–16:00. Reassign or split the run.</div>
            </div>
          </div>
        </section>

        <p className="text-xs pt-4 pb-10" style={{ color: "hsl(var(--muted-foreground))" }}>
          Isolated preview — this theme is scoped to <code className="font-mono">.{theme.className}</code> and does not affect the live application.
        </p>
      </main>
    </div>
  );
};

export const ThemePreview1 = () => <Gallery theme={THEMES.v1} />;
export const ThemePreview2 = () => <Gallery theme={THEMES.v2} />;

export const ThemePreviewIndex = () => (
  <div className="min-h-screen bg-white text-slate-900 font-body px-6 md:px-10 py-12">
    <div className="max-w-4xl mx-auto">
      <p className="theme-overline text-slate-500">EQUIPR · Design Exploration</p>
      <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl mt-3">Theme Comparison</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Two isolated theme directions, both built on the "Industrial Precision" Style Guide. These are
        sandbox previews only — the live app's current styling is untouched.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
        {Object.values(THEMES).map((t) => (
          <Link
            key={t.id}
            to={`/theme-preview/${t.id}`}
            className="block border border-slate-200 rounded-sm p-6 hover:border-slate-900 transition-colors"
            data-testid={`theme-card-${t.id}`}
          >
            <div className="flex items-center justify-between">
              <span className="theme-overline text-slate-500">Option {t.id}</span>
              <span className="text-xs font-mono text-slate-400">{t.mode}</span>
            </div>
            <h2 className="font-heading font-bold text-2xl mt-2">{t.name}</h2>
            <p className="text-sm text-slate-600 mt-2">{t.blurb}</p>
            <div className="flex gap-2 mt-4">
              {t.swatches.map(([label, hex]) => (
                <span key={label} className="h-8 w-8 rounded-sm border border-slate-200" style={{ backgroundColor: hex }} title={`${label} ${hex}`} />
              ))}
            </div>
            <span className="inline-block mt-5 text-sm font-medium underline">View preview →</span>
          </Link>
        ))}
      </div>
    </div>
  </div>
);

export default ThemePreviewIndex;
